import { Injectable, inject, signal, Signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../env';

export interface ActiveIconPack {
  key: string;
  name: string;
  style: 'monochrome' | 'colored';
}

/**
 * Globally available service that resolves the active icon pack once and
 * caches it as a signal. All components that need icon URLs should use this
 * instead of hard-coding a pack key.
 */
@Injectable({ providedIn: 'root' })
export class ActiveIconPackService {
  private readonly http = inject(HttpClient);

  private readonly _pack = signal<ActiveIconPack | null>(null);
  private fetched = false;

  /** The currently active icon pack, or null while loading / on error. */
  readonly pack: Signal<ActiveIconPack | null> = this._pack.asReadonly();

  /** The active pack key, falls back to empty string. */
  readonly packKey: Signal<string> = computed(() => this._pack()?.key ?? '');

  /**
   * Trigger a (re-)fetch of the active icon pack.
   * Called once from app bootstrap or on demand. Safe to call multiple times —
   * subsequent calls are no-ops unless `force` is true.
   */
  load(force = false): void {
    if (this.fetched && !force) return;
    this.fetched = true;
    this.http
      .get<ActiveIconPack>(`${environment.apiUrl}/icon-packs/active`)
      .subscribe({
        next: (pack) => this._pack.set(pack),
        error: () => {
          // Non-fatal: icon URLs will silently 404 rather than crashing the app.
          this._pack.set(null);
        },
      });
  }

  /**
   * Build a public icon URL for the given product kind key.
   * - Colored packs always use `variant=transparent` (theme-independent).
   * - Monochrome packs use `variant=black` (light theme) or `variant=white` (dark theme).
   * Returns an empty string when the active pack is not yet loaded.
   */
  iconUrl(productKindKey: string, themeVariant: 'black' | 'white'): string {
    const pack = this._pack();
    if (!pack?.key) return '';
    const variant = pack.style === 'colored' ? 'transparent' : themeVariant;
    return `${environment.apiUrl}/public/icon-packs/${pack.key}/icons/${encodeURIComponent(productKindKey)}?variant=${variant}`;
  }
}

